
import { pageMetadata } from '@flakiness/server/common/pageMetadata.js';
import { EnvGroup } from '@flakiness/server/common/stats/envGroup.js';
import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { CHECK } from '@flakiness/shared/common/utils.js';
import { ContextType } from '@lit/context';
import { Task } from '@lit/task';
import { LitElement, html, nothing } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { api } from './api.js';
import { linkStyles, pageStyles } from './components/cssstyles.js';
import { ProjectContext } from './components/project-context.js';
import { consume, contexts } from './contexts.js';
import { FeatureTestsReport, FeatureTestsReportState } from './features/feature-tests-report.js';
import { projectPlatformUrl } from './platformUrl.js';
import { RouteConfig, URLState } from './router.js';
import { tasks } from './tasks.js';
import { URLOptions } from './urlOptions.js';

const pageSchema = {

  timelineSplit: URLOptions.timelineSplit,

  query: URLOptions.query,

  /* ---- */
  plane: URLOptions.plane,
  page: URLOptions.page,
  pageSize: URLOptions.pageSize,
  sortName: URLOptions.sortName,
  sortDirection: URLOptions.sortDirection,
  historyType: URLOptions.historyType,
};

@customElement('page-commit')
export class PageCommit extends LitElement {
  static routes(): RouteConfig[] {
    return [{
      path: [
        '/:org/:project/commit/:sha',
        '/:org/:project/commit/:sha/'
      ],
      render: (groups) => ProjectContext.render({
        orgSlug: groups?.org,
        projectSlug: groups?.project,
      }, html`
        <page-commit
          .orgSlug=${groups?.org}
          .projectSlug=${groups?.project}
          .sha=${groups?.sha}
        ></page-commit>
      `),
    }];
  }

  static url(options: Omit<FeatureTestsReportState, 'timelineSplit'> & {
    orgSlug: string,
    projectSlug: string,
    sha: string,
    timelineSplit?: EnvGroup,
    snapshotId: WireTypes.DataSnapshotId | undefined,
  }) {
    const pathname = ['', options.orgSlug, options.projectSlug, 'commit', options.sha].join('/');
    return projectPlatformUrl(pathname, URLState.toSearchParams(pageSchema, {
      timelineSplit: options.timelineSplit,
      page: options.page,
      pageSize: options.pageSize,
      plane: options.plane,
      query: options.query,
      sortDirection: options.sortDirection,
      sortName: options.sortName,
      historyType: options.historyType,
    }), options.snapshotId);
  }

  @query('feature-tests-report') _featureTestsReport?: FeatureTestsReport;

  private _urlState = new URLState(this, pageSchema, () => {
    this._featureTestsReport?.hideDialog();
  });

  @consume({ context: contexts.project, subscribe: true }) private _project?: ContextType<typeof contexts.project>;
  @consume({ context: contexts.dataSnapshotId, subscribe: true }) private _snapshotId?: ContextType<typeof contexts.dataSnapshotId>;

  @property({ attribute: false }) projectSlug?: string;
  @property({ attribute: false }) orgSlug?: string;
  @property({ attribute: false }) sha?: string;

  private _commitStatsTask = new Task(this, {
    args: () => [
      this._project, this._snapshotId, this.sha,
    ] as const,
    task: async ([project, snapshotId, sha], { signal }) => {
      CHECK(project && snapshotId && sha);
      const response = await api.history.commitStats.POST({
        orgSlug: project.org.orgSlug,
        projectSlug: project.projectSlug,
        snapshotId,
        commitOptions: {
          head: sha,
          maxCount: 1,
        },
        pageOptions: {
          number: 0, size: 1,
        },
        timelineSplit: EnvGroup.open().serialize(),
      }, { signal });
      CHECK(response && response.elements[0]);
      return response.elements[0];
    }
  });

  private _commitOptions = new Task(this, {
    args: () => [
      this.sha,
    ] as const,
    task: async ([sha]) => {
      CHECK(sha);
      return {
        head: sha,
        maxCount: 1,
      } satisfies WireTypes.ListCommitOptions;
    }
  });

  private _allEnvironments = tasks.allEnvironments(this, {
    commitOptions: () => this._commitOptions.value,
    project: () => this._project,
    snapshotId: () => this._snapshotId,
  });

  // "All Environments" of the commit's history.
  private _defaultSplit = tasks.defaultSplit(this, {
    preset: () => this.sha ? { type: 'commit', commitId: this.sha } : undefined,
    project: () => this._project,
    snapshotId: () => this._snapshotId,
  });

  // Once the default is known, a URL that spells it out loses it, without a new history entry.
  private _canonicalUrlTask = new Task(this, {
    args: () => [this._urlState.state.timelineSplit, this._defaultSplit.value] as const,
    task: async ([split, defaultSplit]) => {
      if (split && defaultSplit && split.isEqual(defaultSplit))
        this._urlState.update({ timelineSplit: undefined }, { replace: true });
    },
  });

  private _pageMetadataTask = tasks.pageMetadata(this, {
    args: () => [this.orgSlug, this.projectSlug, this.sha, this._commitStatsTask.value] as const,
    metadata: ([orgSlug, projectSlug, sha, commitStats]) => orgSlug && projectSlug && sha ? pageMetadata({
      orgSlug, projectSlug,
      section: `Commit ${sha.substring(0, 7)}${commitStats ? `: ${commitStats.commit.message.split('\n')[0].trim()}` : ''}`,
    }) : undefined,
  });

  override render() {
    const project = this._project;
    const sha = this.sha;
    const split = this._urlState.state.timelineSplit ?? this._defaultSplit.value;
    if (!project || !sha)
      return nothing;

    return html`
      <project-header
        .orgSlug=${this.orgSlug}
        .projectSlug=${this.projectSlug}
        .submenu=${'history'}
      ></project-header>

      <project-body wide>

        <fk-commitstats noduration style="background: var(--sl-color-neutral-50);"
          .stats=${this._commitStatsTask.value}
        ></fk-commitstats>

        ${split ? html`
        <timeline-selector multiple
          .envs=${this._allEnvironments.value}
          .split=${split}
          .defaultSplit=${this._defaultSplit.value}
          @fk-select=${(event: CustomEvent<EnvGroup>) => {
            this._urlState.update({ timelineSplit: event.detail, page: 0 });
          }}
        ></timeline-selector>

        <feature-tests-report
          .commitOptions=${this._commitOptions.value}
          .state=${{
            timelineSplit: split,
            page: this._urlState.state.page,
            pageSize: this._urlState.state.pageSize,
            plane: this._urlState.state.plane,
            query: this._urlState.state.query,
            sortDirection: this._urlState.state.sortDirection,
            sortName: this._urlState.state.sortName,
            historyType: this._urlState.state.historyType,
          } satisfies FeatureTestsReportState}
          .linkifier=${(options: FeatureTestsReportState) => PageCommit.url({
            ...options,
            orgSlug: project.org.orgSlug,
            projectSlug: project.projectSlug,
            sha,
            snapshotId: this._snapshotId,
          })}
        ></feature-tests-report>
        ` : nothing}
      </project-body>

      <app-footer></app-footer>
    `;
  }

  static styles = [pageStyles, linkStyles]
}
